import { useState } from "react";
import { api, formatCurrency, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Edit, Trash2 } from "lucide-react";
import { toast } from "sonner";

export const InformationsTab = ({ employee, salaries, onRefresh }) => {
  const [salaryDialog, setSalaryDialog] = useState(false);
  const [editingSalary, setEditingSalary] = useState(null);
  const [salaryForm, setSalaryForm] = useState({
    effective_from: "", monthly_salary: "", hourly_rate: "", notes: "",
  });

  const openCreate = () => {
    setEditingSalary(null);
    setSalaryForm({
      effective_from: new Date().toISOString().slice(0, 10),
      monthly_salary: "",
      hourly_rate: "",
      notes: "",
    });
    setSalaryDialog(true);
  };

  const openEdit = (s) => {
    setEditingSalary(s);
    setSalaryForm({
      effective_from: s.effective_from || "",
      monthly_salary: s.monthly_salary ?? "",
      hourly_rate: s.hourly_rate ?? "",
      notes: s.notes || "",
    });
    setSalaryDialog(true);
  };

  const handleSave = async () => {
    if (!salaryForm.effective_from) {
      toast.error("Date d'effet requise");
      return;
    }
    if (!salaryForm.monthly_salary && !salaryForm.hourly_rate) {
      toast.error("Salaire mensuel ou taux horaire requis");
      return;
    }
    try {
      const payload = {
        effective_from: salaryForm.effective_from,
        monthly_salary: salaryForm.monthly_salary ? parseFloat(salaryForm.monthly_salary) : null,
        hourly_rate: salaryForm.hourly_rate ? parseFloat(salaryForm.hourly_rate) : null,
        notes: salaryForm.notes || null,
      };
      if (editingSalary) {
        await api.updateEmployeeSalary(editingSalary.id, payload);
        toast.success("Rémunération mise à jour");
      } else {
        await api.createEmployeeSalary(employee.id, payload);
        toast.success("Rémunération ajoutée");
      }
      setSalaryDialog(false);
      setEditingSalary(null);
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de sauvegarde");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cette rémunération ?")) return;
    try {
      await api.deleteEmployeeSalary(id);
      toast.success("Rémunération supprimée");
      onRefresh();
    } catch (_e) {
      toast.error("Erreur de suppression");
    }
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader><CardTitle>Informations personnelles</CardTitle></CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div><span className="text-muted-foreground">Téléphone :</span> {employee.phone || "-"}</div>
            <div><span className="text-muted-foreground">Adresse :</span> {employee.address || "-"}</div>
            <div><span className="text-muted-foreground">Taille haut :</span> {employee.size_top === "custom" ? employee.size_top_custom : (employee.size_top || "-")}</div>
            <div><span className="text-muted-foreground">Taille bas :</span> {employee.size_bottom === "custom" ? employee.size_bottom_custom : (employee.size_bottom || "-")}</div>
            <div><span className="text-muted-foreground">Pointure :</span> {employee.shoe_size || "-"}</div>
            <div><span className="text-muted-foreground">Date d'entrée :</span> {formatDate(employee.entry_date)}</div>
            <div><span className="text-muted-foreground">Date de sortie :</span> {formatDate(employee.exit_date)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle>Commentaires</CardTitle></CardHeader>
          <CardContent>
            <p className="text-sm whitespace-pre-wrap">{employee.comments || "Aucun commentaire."}</p>
          </CardContent>
        </Card>
        {employee.is_interim && (
          <Card className="md:col-span-2 border-amber-300" data-testid="interim-info-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Badge className="bg-amber-100 text-amber-800 border-amber-300">Intérim</Badge>
                Informations agence d'intérim
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
              <div><span className="text-muted-foreground">Taux horaire facturé :</span> <strong>{employee.interim_hourly_rate != null ? `${employee.interim_hourly_rate} €/h` : "-"}</strong></div>
              <div><span className="text-muted-foreground">Agence :</span> {employee.interim_agency_name || "-"}</div>
              <div><span className="text-muted-foreground">Téléphone :</span> {employee.interim_agency_phone || "-"}</div>
              <div><span className="text-muted-foreground">Email :</span> {employee.interim_agency_email || "-"}</div>
              <div className="md:col-span-2"><span className="text-muted-foreground">Adresse :</span> {employee.interim_agency_address || "-"}</div>
            </CardContent>
          </Card>
        )}

        <Card className="md:col-span-2" data-testid="salary-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Rémunération</CardTitle>
            <Button size="sm" onClick={openCreate} data-testid="add-salary-btn">
              <Plus className="h-4 w-4 mr-1" /> Nouvelle rémunération
            </Button>
          </CardHeader>
          <CardContent>
            {salaries.length === 0 ? (
              <p className="text-sm text-muted-foreground italic text-center py-4 bg-muted/30 rounded">
                Aucune rémunération enregistrée. Cliquez sur "Nouvelle rémunération" pour démarrer l'historique.
              </p>
            ) : (
              <div className="space-y-2">
                {salaries.map((s) => (
                  <div
                    key={s.id}
                    className={`flex items-start justify-between p-3 rounded border ${s.is_current ? "border-primary bg-primary/5" : ""}`}
                    data-testid={`salary-row-${s.id}`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2 text-sm">
                        <span className="font-mono font-medium">
                          À partir du {new Date(s.effective_from).toLocaleDateString("fr-FR")}
                        </span>
                        {s.is_current && <Badge className="bg-green-100 text-green-800">En vigueur</Badge>}
                      </div>
                      <div className="mt-1 flex flex-wrap gap-3 text-sm">
                        {s.monthly_salary != null && (
                          <span>Salaire mensuel brut : <strong>{formatCurrency(s.monthly_salary)}</strong></span>
                        )}
                        {s.hourly_rate != null && (
                          <span>Taux horaire : <strong>{formatCurrency(s.hourly_rate)}/h</strong></span>
                        )}
                      </div>
                      {s.notes && <p className="text-xs text-muted-foreground mt-1">{s.notes}</p>}
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(s)} data-testid={`edit-salary-${s.id}`}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost" size="sm"
                        onClick={() => handleDelete(s.id)}
                        data-testid={`delete-salary-${s.id}`}
                      >
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={salaryDialog} onOpenChange={setSalaryDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingSalary ? "Modifier la rémunération" : "Nouvelle rémunération"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Applicable à partir du *</Label>
              <Input
                type="date" value={salaryForm.effective_from}
                onChange={(e) => setSalaryForm({ ...salaryForm, effective_from: e.target.value })}
                data-testid="salary-effective-from"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Salaire mensuel brut (€)</Label>
                <Input
                  type="number" step="0.01" min="0"
                  value={salaryForm.monthly_salary}
                  onChange={(e) => setSalaryForm({ ...salaryForm, monthly_salary: e.target.value })}
                  data-testid="salary-monthly" placeholder="Ex : 2100"
                />
              </div>
              <div>
                <Label>Taux horaire (€/h)</Label>
                <Input
                  type="number" step="0.01" min="0"
                  value={salaryForm.hourly_rate}
                  onChange={(e) => setSalaryForm({ ...salaryForm, hourly_rate: e.target.value })}
                  data-testid="salary-hourly" placeholder="Ex : 14.50"
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">Renseignez au moins l'un des deux champs.</p>
            <div>
              <Label>Note (optionnel)</Label>
              <Input
                value={salaryForm.notes}
                onChange={(e) => setSalaryForm({ ...salaryForm, notes: e.target.value })}
                placeholder="Ex : Promotion, augmentation annuelle..."
                data-testid="salary-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSalaryDialog(false)}>Annuler</Button>
            <Button onClick={handleSave} data-testid="save-salary-btn">
              {editingSalary ? "Enregistrer" : "Ajouter"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InformationsTab;
